"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.FormModelHandler = void 0;
const decode_entities_1 = require("../shared/decode-entities");
const types_1 = require("../shared/types");
const TERM_EXPRESSION_MAP = {
    "calculate-fn": "calculate",
    "relevant-fn": "relevant",
    "required-fn": "required",
    "readonly-fn": "readonly",
};
const VALIDATION_SET_MAP = {
    min: ["min", "text"],
    "min-fn": ["min", "syntax"],
    max: ["max", "text"],
    "max-fn": ["max", "syntax"],
};
class FormModelHandler {
    type = "generic";
    context = {
        inFormModel: false,
        pathArray: [],
        nestedMetatables: [],
    };
    model = {
        type: "model-root",
        name: "",
        terms: {},
        iterables: {},
        meta: {},
        compatibility: {
            metaMaps: true,
        },
        process: {
            syntax: false,
            enums: false,
        },
    };
    ready = false;
    reporter;
    constructor(reporter) {
        this.reporter = reporter;
    }
    onopen = (node, location) => {
        if (node.name === "FormModel") {
            this.context.inFormModel = true;
            this.model.name = node.attributes.name;
            this.context.pathArray = ["FormModel"];
            return;
        }
        if (!this.context.inFormModel) {
            return;
        }
        delete this.context.text;
        this.context.pathArray.push(node.name);
        if (node.name === "term") {
            const { default: def, value, separator } = node.attributes;
            const [valueType, format] = mapTermTypes(node.attributes.type);
            this.context.term = {
                type: "term",
                name: node.attributes.name,
                valueType,
                format,
                use: node.attributes.use,
                compose: node.attributes.compose,
                sync: node.attributes.sync,
                default: def ? (0, decode_entities_1.decodeEntities)(def) : def,
                value: value ? (0, decode_entities_1.decodeEntities)(value) : value,
                separator: separator ? (0, decode_entities_1.decodeEntities)(separator) : separator,
            };
            return;
        }
        if (node.name === "iterable") {
            this.context.iterable = {
                type: "iterable",
                name: node.attributes.name,
                max: node.attributes.max,
                compose: node.attributes.compose,
                terms: {},
            };
        }
        if (node.name === "metatable" ||
            node.name === "metalist" ||
            node.name === "meta") {
            const lastMeta = this.context.nestedMetatables.at(-1);
            if (lastMeta) {
                if (node.name === "metatable" || node.name === "metalist") {
                    if (lastMeta.type === "list" && node.name === "metalist") {
                        this.reporter({
                            message: "Cannot nest a metalist inside a metalist",
                            level: 1,
                            location,
                        });
                        return;
                    }
                    const metatableArray = [];
                    lastMeta.items.push(lastMeta.type === "table"
                        ? {
                            type: "metatable-item",
                            name: node.attributes.name,
                            value: metatableArray,
                        }
                        : {
                            type: "metalist-item",
                            value: metatableArray,
                        });
                    this.context.nestedMetatables.push({
                        type: node.name === "metatable" ? "table" : "list",
                        items: metatableArray,
                    });
                }
                if (node.name === "meta") {
                    lastMeta.items.push(lastMeta.type === "table"
                        ? {
                            type: "metatable-item",
                            name: node.attributes.name,
                            value: (0, decode_entities_1.decodeEntities)(node.attributes.value || ""),
                        }
                        : {
                            type: "metalist-item",
                            value: (0, decode_entities_1.decodeEntities)(node.attributes.value || ""),
                        });
                }
                return;
            }
            const meta = this.context.term
                ? this.context.term.meta || (this.context.term.meta = {})
                : this.model.meta;
            if (node.name === "metatable" || node.name === "metalist") {
                const metatableArray = [];
                meta[node.attributes.name] = metatableArray;
                this.context.nestedMetatables.push({
                    type: node.name === "metatable" ? "table" : "list",
                    items: metatableArray,
                });
            }
            if (node.name === "meta") {
                meta[node.attributes.name] = (0, decode_entities_1.decodeEntities)(node.attributes.value || "");
            }
            if (node.attributes.compose) {
                if (!meta[types_1.metaComposeDirectives]) {
                    meta[types_1.metaComposeDirectives] = {};
                }
                meta[types_1.metaComposeDirectives][node.attributes.name] =
                    node.attributes.compose;
            }
            return;
        }
        if (node.name === "compose") {
            if (!this.model.composables) {
                this.model.composables = [];
            }
            const path = (0, decode_entities_1.decodeEntities)(node.attributes.path);
            this.model.composables.push({
                type: "model-composable",
                path,
            });
            return;
        }
        if (this.context.term) {
            if (node.name === "enum") {
                this.context.enum = {
                    type: "term-enum",
                    label: (0, decode_entities_1.decodeEntities)(node.attributes.label || ""),
                    value: (0, decode_entities_1.decodeEntities)(node.attributes.value || ""),
                };
                return;
            }
            if (node.name === "validation") {
                this.context.validation = {
                    type: "term-validation",
                    validationType: node.attributes.type,
                };
                return;
            }
            if (node.name === "event") {
                this.context.event = {
                    type: "term-event",
                    on: node.attributes.on?.split(" "),
                    onAction: node.attributes.onAction?.split(" "),
                    onTermInput: node.attributes.onTermInput?.split(" "),
                    onIterableCount: node.attributes.onIterableCount?.split(" "),
                    statements: [],
                };
            }
        }
    };
    ontext = (node) => {
        if (!this.context.inFormModel) {
            return;
        }
        this.context.text = node;
    };
    onclose = (node, location) => {
        if (!this.context.inFormModel) {
            return;
        }
        const lastTag = this.context.pathArray.pop();
        if (lastTag !== node.name) {
            this.reporter({
                message: "The closing tag does not match the open tag",
                location,
                level: 1,
            });
        }
        if (node.name === "FormModel") {
            this.context.inFormModel = false;
            this.ready = true;
            return;
        }
        if (node.name === "metatable" || node.name === "metalist") {
            this.context.nestedMetatables.pop();
            return;
        }
        if (this.context.iterable) {
            if (node.name === "term" && this.context.term) {
                this.context.iterable.terms[this.context.term.name] = this.context.term;
                delete this.context.term;
                return;
            }
            if (node.name === "iterable") {
                if (!this.context.iterableCount) {
                    this.reporter({
                        message: `No "count" element for the iterable: ${this.context.iterable.name}`,
                        location,
                        level: 1,
                    });
                    delete this.context.iterable;
                    return;
                }
                this.model.iterables[this.context.iterable.name] = {
                    ...this.context.iterable,
                    count: this.context.iterableCount,
                };
                delete this.context.iterable;
                delete this.context.iterableCount;
                return;
            }
            if (node.name === "count-fn" && this.context.text) {
                this.context.iterableCount = {
                    type: "syntax",
                    value: this.context.text,
                };
                return;
            }
        }
        if (this.context.term) {
            if (node.name === "term") {
                this.model.terms[this.context.term.name] = this.context.term;
                delete this.context.term;
                return;
            }
            if (this.context.validation) {
                if (node.name === "validation") {
                    if (!this.context.term.validationList) {
                        this.context.term.validationList = [];
                    }
                    this.context.term.validationList.push(this.context.validation);
                    delete this.context.validation;
                    return;
                }
                // Handle all validation nodes
                if (this.context.text) {
                    const validationNode = VALIDATION_SET_MAP[node.name];
                    if (validationNode) {
                        if (this.context.validation[validationNode[0]]) {
                            this.reporter({
                                message: `Validation already contains a value for ${validationNode[0]}`,
                                location,
                                level: 1,
                            });
                            return;
                        }
                        this.context.validation[validationNode[0]] = {
                            type: validationNode[1],
                            value: this.context.text,
                        };
                    }
                }
                return;
            }
            if (this.context.event) {
                if (node.name === "event") {
                    if (!this.context.term.events) {
                        this.context.term.events = [];
                    }
                    this.context.term.events.push(this.context.event);
                    delete this.context.event;
                    return;
                }
                if (node.name === "suppose-fn" && this.context.text) {
                    this.context.event.statements.push({
                        type: "term-event-suppose",
                        condition: {
                            type: "syntax",
                            value: this.context.text,
                        },
                    });
                    return;
                }
                if (node.name === "when-fn" && this.context.text) {
                    this.context.event.statements.push({
                        type: "term-event-when",
                        condition: {
                            type: "syntax",
                            value: this.context.text,
                        },
                    });
                    return;
                }
                if (node.name === "set") {
                    this.context.event.statements.push({
                        type: "term-event-set",
                        value: {
                            type: "text",
                            value: (0, decode_entities_1.decodeEntities)(this.context.text || ""),
                        },
                    });
                    return;
                }
                if (node.name === "set-fn") {
                    this.context.event.statements.push({
                        type: "term-event-set",
                        value: {
                            type: "syntax",
                            value: this.context.text || "",
                        },
                    });
                    return;
                }
            }
            if (node.name === "label" || node.name === "label-fn") {
                if (this.context.term.label) {
                    this.reporter({
                        message: "cannot set two labels on a term",
                        location,
                        level: 1,
                    });
                    return;
                }
                this.context.term.label =
                    node.name === "label"
                        ? {
                            type: "text",
                            value: (0, decode_entities_1.decodeEntities)(this.context.text || ""),
                        }
                        : {
                            type: "syntax",
                            value: this.context.text || "",
                        };
                return;
            }
            if (node.name === "enum") {
                if (!this.context.term.enums) {
                    this.context.term.enums = [];
                }
                if (!this.context.enum) {
                    this.reporter({ message: "No enum in context", location, level: 1 });
                    return;
                }
                this.context.term.enums.push(this.context.enum);
                delete this.context.enum;
                return;
            }
            if (node.name === "enums-fn") {
                this.context.term.enumsFn = {
                    type: "syntax",
                    value: this.context.text || "",
                };
                return;
            }
            // Handle all term expressions nodes
            const expression = TERM_EXPRESSION_MAP[node.name];
            if (expression) {
                if (!this.context.text) {
                    this.reporter({
                        message: `No expression for ${expression}`,
                        location,
                        level: 1,
                    });
                    return;
                }
                const node = {
                    type: "syntax",
                    value: this.context.text,
                };
                if (expression === "relevant" &&
                    this.context.enum &&
                    this.context.pathArray.at(-1) === "enum") {
                    this.context.enum.relevant = node;
                    return;
                }
                this.context.term[expression] = node;
                return;
            }
        }
        if (node.name === "key-fn" && this.context.text) {
            const keyExpression = {
                type: "syntax",
                value: this.context.text,
            };
            if (this.context.term) {
                this.context.term.key = keyExpression;
            }
            else if (this.context.iterable) {
                this.context.iterable.key = keyExpression;
            }
            else {
                this.model.key = keyExpression;
            }
            return;
        }
    };
}
exports.FormModelHandler = FormModelHandler;
function mapTermTypes(type) {
    switch (type) {
        case "integer":
        case "decimal":
            return ["number", type];
        case "date":
        case "date:mdy":
            return ["date", "mdy"];
        case "date:iso":
            return ["date", "iso"];
        case "currency:integer":
        case "currency-integer":
            return ["currency", "integer"];
        case "currency:double":
        case "currency-double":
            return ["currency", "double"];
        default:
            return [type, undefined];
    }
}
//# sourceMappingURL=handler.js.map